import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { ChevronLeft, ChevronRight, Search } from "@mui/icons-material";
import "@splidejs/react-splide/css";

import "./styles/slushFlavours.css";

import axios from "axios";

import { API_BASE_URL } from "../config";
import slushCupWhite from "../images/slush.webp";

const defaultFlavourImage = slushCupWhite;

const flavourColors = {
  Coke: "#e85d75",
  Cherry: "#ef476f",
  "Blue Raspberry": "#4da6ff",
  Lemon: "#f6c945",
  Lime: "#69c7a5",
  Grape: "#9b6cff",
  Strawberry: "#f58aa8",
  "Bubble Gum": "#f3a0bd",
  Blackcurrant: "#7051a8",
  Mango: "#f59e42",
  "Passion Fruit": "#f08a4b",
  Pineapple: "#f5c542",
  Watermelon: "#56c596",
  Raspberry: "#d94f8a",
  "Peach Tea": "#ee9b6b",
  "Pink Lemonade": "#f3a0bd",
};

const fallbackFlavourColors = [
  "#ef476f",
  "#4da6ff",
  "#f6c945",
  "#9b6cff",
  "#f58aa8",
  "#f59e42",
  "#56c596",
  "#e85d75",
];


/*
|--------------------------------------------------------------------------
| FEATURED FLAVOURS
|--------------------------------------------------------------------------
*/

// const featuredNames = [
//   "Coke",
//   "Cherry",
//   "Blue Raspberry",
//   "Lemon",
//   "Grape",
//   "Strawberry",
//   "Bubble Gum",
//   "Watermelon",
// ];

const getFlavourColor = (flavour, index = 0) => {
  const apiColor = flavour?.color?.trim();

  if (/^#[0-9a-f]{6}$/i.test(apiColor || "")) {
    return apiColor;
  }

  const name = flavour?.name;
  const matchedName = Object.keys(flavourColors).find(
    (flavourName) =>
      flavourName.toLowerCase() === name?.trim().toLowerCase()
  );

  return matchedName
    ? flavourColors[matchedName]
    : fallbackFlavourColors[index % fallbackFlavourColors.length];
};


export default function SlushFlavours() {

  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  const [flavours, setFlavours] = useState([]);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFlavour, setSelectedFlavour] = useState(null);
  const flavourRailRef = useRef(null);
  const flavoursPerPage = 4;

  useEffect(() => {
    let mounted = true;

    async function getFlavours() {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/tubbeesSite/slush`);

        if (!mounted) {
          return;
        }
        const apiFlavours =
          Array.isArray(res.data)
            ? res.data
            : Array.isArray(
                res.data?.slush
              )
              ? res.data.slush
              : Array.isArray(
                  res.data?.flavours
                )
                ? res.data.flavours
                : Array.isArray(
                    res.data?.data
                  )
                  ? res.data.data
                  : [];


        /*
        |--------------------------------------------------------------------------
        | Remove duplicate flavour names
        |--------------------------------------------------------------------------
        */

        const uniqueFlavours =
          Array.from(
            new Map(
              apiFlavours
                .filter(
                  (item) =>
                    item &&
                    item.name
                )
                .map(
                  (item) => [
                    item.name
                      .trim()
                      .toLowerCase(),
                    item,
                  ]
                )
            ).values()
          );

        setFlavours(
          uniqueFlavours
        );
        setSelectedFlavour((current) => current || uniqueFlavours[0] || null);

      } catch (error) {

        console.error(
          "Unable to load slushee flavours:",
          error
        );

      } finally {

        if (mounted) {
          setLoading(false);
        }

      }

    }


    getFlavours();


    return () => {
      mounted = false;
    };

  }, []);
  useEffect(() => {
    if (!selectedFlavour && flavours.length) {
      setSelectedFlavour(flavours[0]);
    }
  }, [flavours, selectedFlavour]);


  /*
  |--------------------------------------------------------------------------
  | FEATURED FLAVOURS
  |--------------------------------------------------------------------------
  */

  // const featuredFlavours =
  //   useMemo(() => {

  //     if (!flavours.length) {
  //       return [];
  //     }


  //     const featured = [];


  //     /*
  //     |--------------------------------------------------------------------------
  //     | First use our preferred featured flavours
  //     |--------------------------------------------------------------------------
  //     */

  //     featuredNames.forEach(
  //       (featuredName) => {

  //         const found =
  //           flavours.find(
  //             (flavour) =>
  //               flavour?.name
  //                 ?.trim()
  //                 .toLowerCase() ===
  //               featuredName
  //                 .trim()
  //                 .toLowerCase()
  //           );


  //         if (found) {
  //           featured.push(found);
  //         }

  //       }
  //     );


  //     /*
  //     |--------------------------------------------------------------------------
  //     | If some featured names don't exist,
  //     | fill the remaining spaces automatically.
  //     |--------------------------------------------------------------------------
  //     */

  //     if (featured.length < 8) {

  //       flavours.forEach(
  //         (flavour) => {

  //           const alreadyAdded =
  //             featured.some(
  //               (item) =>
  //                 item.name
  //                   ?.toLowerCase() ===
  //                 flavour.name
  //                   ?.toLowerCase()
  //             );


  //           if (
  //             !alreadyAdded &&
  //             featured.length < 8
  //           ) {
  //             featured.push(
  //               flavour
  //             );
  //           }

  //         }
  //       );

  //     }


  //     return featured;

  //   }, [flavours]);


  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const filteredFlavours =
    useMemo(() => {

      const query =
        search
          .trim()
          .toLowerCase();


      if (!query) {
        return flavours;
      }


      return flavours.filter(
        (flavour) => {

          const name =
            flavour?.name
              ?.toLowerCase() ||
            "";


          const description =
            flavour?.description
              ?.toLowerCase() ||
            "";


          return (
            name.includes(query) ||
            description.includes(query)
          );

        }
      );

    }, [
      flavours,
      search,
    ]);


  /*
  |--------------------------------------------------------------------------
  | TOTAL PAGES
  |--------------------------------------------------------------------------
  */

  const totalPages =
    Math.ceil(
      filteredFlavours.length /
        flavoursPerPage
    );


  /*
  |--------------------------------------------------------------------------
  | CURRENT PAGE DATA
  |--------------------------------------------------------------------------
  */

  const startIndex =
    (currentPage - 1) *
    flavoursPerPage;


  const currentFlavours =
    filteredFlavours.slice(
      startIndex,
      startIndex +
        flavoursPerPage
    );


  const getFlavourImage = (flavour) => {
    const image = typeof flavour?.image === "string"
      ? flavour.image.trim()
      : "";

    return image || defaultFlavourImage;
  };
  /*
  |--------------------------------------------------------------------------
  | OPEN CATALOGUE
  |--------------------------------------------------------------------------
  */

  // const openCatalogue = () => {

  //   setCurrentPage(1);

  //   setSearch("");

  //   setShowAll(true);

  // };


  /*
  |--------------------------------------------------------------------------
  | CLOSE CATALOGUE
  |--------------------------------------------------------------------------
  */

  const closeCatalogue = () => {

    setShowAll(false);

    setSearch("");

    setCurrentPage(1);

  };


  /*
  |--------------------------------------------------------------------------
  | SEARCH CHANGE
  |--------------------------------------------------------------------------
  */

  const handleSearch = (
    event
  ) => {

    setSearch(
      event.target.value
    );

    setCurrentPage(1);

  };


  /*
  |--------------------------------------------------------------------------
  | ESCAPE KEY
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!showAll) {
      return;
    }


    const handleEscape = (
      event
    ) => {

      if (
        event.key ===
        "Escape"
      ) {

        closeCatalogue();

      }

    };


    document.addEventListener(
      "keydown",
      handleEscape
    );


    document.body.style.overflow =
      "hidden";


    return () => {

      document.removeEventListener(
        "keydown",
        handleEscape
      );

      document.body.style.overflow =
        "";

    };

  }, [showAll]);


  /*
  |--------------------------------------------------------------------------
  | FLAVOUR CARD
  |--------------------------------------------------------------------------
  */
const FlavourCard = ({
  flavour,
  index,
}) => {

  const image =
    getFlavourImage(flavour);

  return (
    <article
      className="slush-flavour-card"
      style={{
        "--flavour-color": getFlavourColor(flavour, index),
      }}
    >

      <div className="slush-flavour-image">

        <img
          src={image}
          alt={`${flavour.name} slushee flavour`}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = slushCupWhite;
          }}
        />

      </div>


      <div className="slush-flavour-content">

        <h3>
          {flavour.name}
        </h3>


        {flavour.description && (
          <p>
            {flavour.description}
          </p>
        )}

      </div>

    </article>
  );
};


  /*
  |--------------------------------------------------------------------------
  | PAGINATION COMPONENT
  |--------------------------------------------------------------------------
  */

  const Pagination = () => {

    if (totalPages <= 1) {
      return null;
    }


    /*
    |--------------------------------------------------------------------------
    | Create page numbers
    |--------------------------------------------------------------------------
    */

    const pages = [];


    /*
    |--------------------------------------------------------------------------
    | Small number of pages
    |--------------------------------------------------------------------------
    */

    if (totalPages <= 7) {

      for (
        let i = 1;
        i <= totalPages;
        i++
      ) {

        pages.push(i);

      }

    } else {

      /*
      |--------------------------------------------------------------------------
      | First pages
      |--------------------------------------------------------------------------
      */

      pages.push(1);


      if (currentPage > 4) {
        pages.push("...");
      }


      /*
      |--------------------------------------------------------------------------
      | Pages around current page
      |--------------------------------------------------------------------------
      */

      const start =
        Math.max(
          2,
          currentPage - 1
        );


      const end =
        Math.min(
          totalPages - 1,
          currentPage + 1
        );


      for (
        let i = start;
        i <= end;
        i++
      ) {

        if (
          !pages.includes(i)
        ) {

          pages.push(i);

        }

      }


      /*
      |--------------------------------------------------------------------------
      | Last pages
      |--------------------------------------------------------------------------
      */

      if (
        currentPage <
        totalPages - 3
      ) {

        pages.push("...");

      }


      pages.push(
        totalPages
      );

    }


    return (

      <div className="flavour-pagination">

        {/* PREVIOUS */}

        <button
          type="button"
          className="pagination-arrow"
          disabled={
            currentPage === 1
          }
          onClick={() => {

            setCurrentPage(
              (page) =>
                Math.max(
                  1,
                  page - 1
                )
            );

          }}
          aria-label="Previous page"
        >
          ←
        </button>


        {/* PAGE NUMBERS */}

        <div className="pagination-pages">

          {pages.map(
            (page, index) => {

              if (
                page === "..."
              ) {

                return (

                  <span
                    key={`ellipsis-${index}`}
                    className="pagination-ellipsis"
                  >
                    ...
                  </span>

                );

              }


              return (

                <button
                  key={page}
                  type="button"
                  className={
                    currentPage ===
                    page
                      ? "pagination-page active"
                      : "pagination-page"
                  }
                  onClick={() =>
                    setCurrentPage(
                      page
                    )
                  }
                >
                  {page}
                </button>

              );

            }
          )}

        </div>


        {/* NEXT */}

        <button
          type="button"
          className="pagination-arrow"
          disabled={
            currentPage ===
            totalPages
          }
          onClick={() => {

            setCurrentPage(
              (page) =>
                Math.min(
                  totalPages,
                  page + 1
                )
            );

          }}
          aria-label="Next page"
        >
          →
        </button>

      </div>

    );

  };


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <>

      {/* ========================================================
          MAIN SLUSHEE SECTION
      ======================================================== */}

      <section
        id="slush"
        className="slush-section flavour-wall"
        aria-labelledby="slush-title"
      >
        <div className="slush-container">
          <div className="flavour-wall-heading">
            <span className="flavour-eyebrow">THE TUBBEES FLAVOUR WALL</span>
            <h2 id="slush-title">Find your <span>favourite.</span></h2>
            <p>100+ bold combinations. One perfect pour.</p>
          </div>

          {loading ? (
            <div className="slush-loading"><div className="slush-loader" /><span>Loading flavours...</span></div>
          ) : (
            <div
              className="flavour-screen"
              style={{ "--selected-colour": getFlavourColor(selectedFlavour, 0) }}
            >
              <div className="flavour-screen-glow" />
              <div className="flavour-screen-copy">
                <span className="flavour-screen-index">FLAVOUR {String((flavours.indexOf(selectedFlavour) + 1) || 1).padStart(2, "0")}</span>
                <h3>{selectedFlavour?.name || "Choose a flavour"}</h3>
                <p>{selectedFlavour?.description || "Drag the flavour rail and find your next obsession."}</p>
              </div>
              <div className="flavour-hero-product">
                <img
                  key={selectedFlavour?.name || "default"}
                  src={getFlavourImage(selectedFlavour)}
                  alt={selectedFlavour ? `${selectedFlavour.name} product` : "Tubbees product"}
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = defaultFlavourImage;
                  }}
                />
              </div>
              <div className="flavour-wall-search">
                <Search aria-hidden="true" />
                <input value={search} onChange={handleSearch} placeholder="Search products..." aria-label="Search products" />
                {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search">×</button>}
              </div>
              <div className="flavour-rail-wrap">
                <button type="button" className="flavour-rail-arrow" onClick={() => flavourRailRef.current?.splide?.go("<")} aria-label="Previous flavour"><ChevronLeft /></button>
                <Splide
                  ref={flavourRailRef}
                  className="flavour-rail"
                  options={{ type: "slide", rewind: true, perPage: 5, perMove: 1, gap: ".65rem", arrows: false, pagination: false, drag: true, focus: "center", trimSpace: false, breakpoints: { 900: { perPage: 4 }, 600: { perPage: 2.4, gap: ".45rem" } } }}
                  onMove={(_, newIndex) => {
                    const visible = filteredFlavours.length ? filteredFlavours : flavours;
                    if (visible[newIndex % visible.length]) setSelectedFlavour(visible[newIndex % visible.length]);
                  }}
                >
                  {(filteredFlavours.length ? filteredFlavours : flavours).map((flavour, index) => (
                    <SplideSlide key={`${flavour.name}-${index}`}>
                      <button type="button" className={selectedFlavour?.name === flavour.name ? "flavour-pill is-selected" : "flavour-pill"} onClick={() => { setSelectedFlavour(flavour); flavourRailRef.current?.splide?.go(index); }} style={{ "--pill-colour": getFlavourColor(flavour, index) }}>{flavour.name}</button>
                    </SplideSlide>
                  ))}
                </Splide>
                <button type="button" className="flavour-rail-arrow" onClick={() => flavourRailRef.current?.splide?.go(">") } aria-label="Next flavour"><ChevronRight /></button>
              </div>
            </div>
          )}

          {/* <button type="button" className="flavour-wall-catalogue" onClick={openCatalogue}>Explore all flavours <span>↗</span></button> */}
        </div>
      </section>


      {/* ========================================================
          FULL FLAVOUR CATALOGUE MODAL
      ======================================================== */}

      {showAll && (

        <div
          className="flavour-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="flavour-modal-title"
        >

          {/* BACKDROP */}

          <div
            className="flavour-modal-backdrop"
            onClick={
              closeCatalogue
            }
          />


          {/* MODAL */}

          <div className="flavour-modal-panel">


            {/* ==================================================
                MODAL HEADER
            ================================================== */}

            <div className="flavour-modal-header">

              <div>

                <span className="modal-eyebrow">
                  THE COMPLETE COLLECTION
                </span>


                <h2 id="flavour-modal-title">

                  90+{" "}

                  <span>
                    FLAVOURS
                  </span>

                </h2>


                <p>
                  Find your perfect
                  slushee.
                </p>

              </div>


              <button
                type="button"
                className="modal-close"
                onClick={
                  closeCatalogue
                }
                aria-label="Close flavour catalogue"
              >

                <span />
                <span />

              </button>

            </div>


            {/* ==================================================
                SEARCH
            ================================================== */}

            <div className="catalogue-search">

              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              >

                <circle
                  cx="11"
                  cy="11"
                  r="6.5"
                />

                <path d="m16 16 5 5" />

              </svg>


              <input
                type="search"
                value={search}
                onChange={
                  handleSearch
                }
                placeholder="Search all flavours..."
                autoFocus
                autoComplete="off"
              />


              {search && (

                <button
                  type="button"
                  onClick={() => {

                    setSearch("");

                    setCurrentPage(1);

                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>

              )}

            </div>


            {/* ==================================================
                RESULT INFORMATION
            ================================================== */}

            <div className="catalogue-info">

              <span>

                {search
                  ? `${filteredFlavours.length} result${
                      filteredFlavours.length === 1
                        ? ""
                        : "s"
                    }`
                  : `${flavours.length} flavours`}

              </span>


              <span className="catalogue-page-info">

                {filteredFlavours.length > 0
                  ? `Page ${currentPage} of ${totalPages}`
                  : ""}

              </span>


              {search && (

                <button
                  type="button"
                  onClick={() => {

                    setSearch("");

                    setCurrentPage(1);

                  }}
                >
                  SHOW ALL
                </button>

              )}

            </div>


            {/* ==================================================
                FLAVOUR GRID
            ================================================== */}

            <div className="catalogue-grid">

              {currentFlavours.length > 0 ? (

                currentFlavours.map(
                  (flavour, index) => (

                    <FlavourCard
                      key={`${flavour.name}-${index}`}
                      flavour={flavour}
                      index={index}
                    />

                  )
                )

              ) : (

                <div className="catalogue-empty">

                  <div>
                    ?
                  </div>


                  <h3>
                    No flavour found
                  </h3>


                  <p>
                    Try searching for
                    something different.
                  </p>


                  <button
                    type="button"
                    onClick={() => {

                      setSearch("");

                      setCurrentPage(1);

                    }}
                  >
                    SHOW ALL FLAVOURS
                  </button>

                </div>

              )}

            </div>


            {/* ==================================================
                PAGINATION
            ================================================== */}

            <Pagination />


          </div>

        </div>

      )}

    </>

  );

}